<script setup lang="ts">
/**
 * What a customer sees when the framework itself refuses the address — until now the bare
 * default page, so the portal had two different 404s. This is the same panel the router's
 * catch-all draws; a failure that is neither 404 nor 403 says so in the same shape rather
 * than in a stack trace.
 */
import type { NuxtError } from '#app'
import RefusalPanel from '~/components/ui/RefusalPanel.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'

const props = defineProps<{ error: NuxtError }>()
const known = computed(() => props.error.statusCode === 404 || props.error.statusCode === 403)
const kind = computed(() => (props.error.statusCode === 403 ? 'forbidden' : 'missing'))
</script>

<template>
  <NuxtLayout name="auth" :showcase="false">
    <RefusalPanel v-if="known" :kind="kind" />
    <section v-else class="overflow-hidden rounded-lg border border-border bg-card">
      <header class="flex h-[var(--card-header-height)] items-center gap-2.5 border-b border-border px-3">
        <span class="flex items-center gap-1.5 text-xs font-semibold"><Icon name="alert" :size="12" />Something went wrong</span>
        <span class="num text-[length:var(--type-meta)] text-muted-foreground">http {{ error.statusCode }}</span>
      </header>
      <div class="flex flex-col gap-3 p-3.5">
        <p class="m-0 text-xs leading-relaxed text-pretty">The page could not be shown. Nothing you did was lost; opening it again is safe.</p>
        <ActionButton label="Go to Overview" variant="gradient" size="lg" @click="clearError({ redirect: '/overview' })">
          <template #icon><Icon name="arrow" :size="13" /></template>
        </ActionButton>
      </div>
    </section>
  </NuxtLayout>
</template>
